/**
 * Desktop App Layout
 * Optimized for Tauri desktop application with:
 * - Desktop-specific header (no "Home" link)
 * - Persistent navigation
 * - Keyboard shortcuts
 * - No public homepage elements
 */

'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { isTauriDesktop } from '@/lib/desktop/desktopUtils';

export default function DesktopLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated, isLoading } = useAuth();
  const [isDesktop, setIsDesktop] = useState(false);

  // Detect if running in Tauri desktop app (client-side only)
  useEffect(() => {
    setIsDesktop(isTauriDesktop());
  }, []);

  useEffect(() => {
    // Only apply desktop redirects if running in Tauri
    if (!isDesktop) return;

    // Skip redirects for call/meeting pages - these are opened in Tauri child windows
    // and handle their own authentication via URL params (token in URL)
    const isCallPage = pathname?.startsWith('/call/') || pathname?.startsWith('/meeting/');
    if (isCallPage) return;

    // Redirect from root to dashboard
    if (pathname === '/') {
      if (isAuthenticated) {
        router.replace('/client-zone/dashboard');
      } else if (!isLoading) {
        router.replace('/auth/login?platform=desktop');
      }
    }

    // Block public pages for desktop app
    const publicPages = ['/about', '/pricing', '/features', '/contact', '/demo'];
    if (publicPages.some(page => pathname.startsWith(page))) {
      router.replace('/client-zone/dashboard');
    }

    // Register keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K: Quick search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        // TODO: Open quick search modal
        console.log('[Desktop] Quick search shortcut');
      }

      // Ctrl/Cmd + , : Settings
      if ((e.ctrlKey || e.metaKey) && e.key === ',') {
        e.preventDefault();
        router.push('/client-zone/settings');
      }

      // Ctrl/Cmd + H: Dashboard
      if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
        e.preventDefault();
        router.push('/client-zone/dashboard');
      }

      // F5: Refresh (allow default behavior)
      // Ctrl/Cmd + R: Refresh (allow default behavior)
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pathname, router, isAuthenticated, isLoading, isDesktop]);

  // Add desktop-specific CSS class to body
  useEffect(() => {
    if (isDesktop) {
      document.body.classList.add('desktop-app');
      
      // Add custom CSS for desktop
      const style = document.createElement('style');
      style.textContent = `
        .desktop-app {
          /* Disable text selection for UI elements (keep it for content) */
          user-select: none;
          -webkit-user-select: none;
          
          /* Custom scrollbar for desktop */
          --scrollbar-width: 10px;
        }
        
        .desktop-app ::-webkit-scrollbar {
          width: var(--scrollbar-width);
          height: var(--scrollbar-width);
        }
        
        .desktop-app ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 5px;
        }
        
        .desktop-app ::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 5px;
        }
        
        .desktop-app ::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        
        /* Re-enable text selection for content areas */
        .desktop-app input,
        .desktop-app textarea,
        .desktop-app [contenteditable],
        .desktop-app .content,
        .desktop-app .selectable {
          user-select: text;
          -webkit-user-select: text;
        }
        /* Desktop-specific styles */
        .desktop-app {
          user-select: none; /* Prevent text selection for desktop feel */
        }
        
        /* Remove window controls area (handled by Tauri) */
        .desktop-app .window-controls {
          display: none;
        }
        
        /* Compact spacing for desktop */
        .desktop-app .sidebar {
          width: 240px; /* Fixed width for desktop */
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        document.body.classList.remove('desktop-app');
        document.head.removeChild(style);
      };
    }
  }, [isDesktop]);

  // If not desktop, just render children without desktop chrome
  if (!isDesktop) {
    return <>{children}</>;
  }

  // Desktop app - just pass through children, Header.tsx will handle desktop mode
  return <>{children}</>;
}
